// The WhatsApp pre-screen message, and the chat link that carries it.
//
// The step between "the resume was read" and "the AI interview link went out":
// a recruiter messages the candidate about the job — the skills it needs, two
// call times — and only invites the ones who say yes.
//
// ─── NO AUTOMATION, ON PURPOSE ──────────────────────────────────────────────
//
// WhatsApp's own click-to-chat link opens a chat with any number and puts the
// message straight into the typing box. The recruiter reads it and presses
// Enter. Nothing is sent without a person: keyboard simulation and bulk senders
// break WhatsApp's terms, and getting the company's main number banned is not a
// trade worth making to save one key press. Sending automatically is what the
// official WhatsApp Business API is for, if it is ever wanted.
//
// ─── WHY A MODULE ───────────────────────────────────────────────────────────
//
// Same reason as inviteMessage.js: the words a stranger reads about a job they
// applied for are worth being able to read and test without rendering a table.

// Placeholders the template may use, in the order the help text lists them.
export const PLACEHOLDERS = [
  { key: "name", hint: "the candidate's first name, tidied — “Raghul.G” becomes Raghul" },
  { key: "full_name", hint: "their name as it is on file" },
  { key: "job_title", hint: "this job's title" },
  { key: "skills", hint: "the job's required skills" },
  { key: "experience_required", hint: "the job's experience requirement" },
  { key: "signature", hint: "the job's email sign-off" },
];

// Short first words that read better with the next one: "Hi Sri Dhanya", not
// "Hi Sri". A fixed list rather than "any word of three letters or fewer" —
// that rule turned "Raj Malhotra" into "Hi Raj Malhotra".
const JOINED_PREFIXES = new Set(["sri", "shri", "sree", "sai", "md", "mohd", "mohammed", "syed"]);

/**
 * The name a message opens with.
 *   "Raghul.G"     -> "Raghul"
 *   "SRI DHANYA V" -> "Sri Dhanya"
 *   "PALAK PATLE"  -> "Palak"
 *   "(unknown)"    -> "there"   (the resume reader's placeholder, never a name)
 */
export function callName(fullName) {
  const raw = String(fullName ?? "").trim();
  if (!raw || raw === "(unknown)") return "there";
  const words = raw
    .replace(/[._]/g, " ")
    .split(/\s+/)
    // Initials ("G", "V") are dropped; they are not what anybody is called.
    .filter((w) => w.length > 1 && /[a-z]/i.test(w))
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
  if (!words.length) return "there";
  return JOINED_PREFIXES.has(words[0].toLowerCase()) && words[1]
    ? `${words[0]} ${words[1]}`
    : words[0];
}

/**
 * Fills {placeholders}. One with no value is LEFT VISIBLE — "{skills}" in the
 * preview is how somebody notices the job has no skills on it before a
 * candidate does. Unknown names are left alone the same way.
 */
export function fillTemplate(template, values) {
  return String(template || "").replace(/\{(\w+)\}/g, (match, key) => {
    const value = values[key];
    return value === undefined || value === null || String(value).trim() === ""
      ? match
      : String(value).trim();
  });
}

/** Placeholders in a message that nothing filled — shown as a warning before sending. */
export function unfilledPlaceholders(message) {
  return [...new Set((String(message).match(/\{(\w+)\}/g) || []).map((m) => m.slice(1, -1)))];
}

/**
 * A starting point for a job's message, built from what the job already says.
 * Placeholders rather than the values themselves, so editing the job later
 * changes the message too. Lines the job has nothing for are left out.
 */
export function suggestedTemplate(job = {}) {
  const lines = [
    "Hi {name},",
    "",
    "We came across your profile for the {job_title} role and would like to speak with you.",
  ];
  const details = [];
  if (String(job.required_skills || "").trim()) details.push("Skills we need: {skills}");
  if (String(job.experience_required || "").trim()) {
    details.push("Experience: {experience_required}");
  }
  if (details.length) lines.push("", ...details);
  lines.push(
    "",
    "Are you free for a short phone call? Reply 1 or 2 to pick a time:",
    "1. 6 pm",
    "2. 7 pm",
  );
  if (String(job.mail_signature || "").trim()) lines.push("", "{signature}");
  return lines.join("\n");
}

/** The message for one candidate. */
export function buildPrescreenMessage(row, job = {}) {
  return fillTemplate(job.whatsapp_template || "", {
    name: callName(row?.candidate_name),
    full_name: row?.candidate_name === "(unknown)" ? "" : row?.candidate_name,
    job_title: job.title,
    // "a, b ,c" as typed in the job form, read as a list.
    skills: String(job.required_skills || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .join(", "),
    experience_required: job.experience_required,
    signature: job.mail_signature,
  });
}

// ─── Where chats open on a computer ────────────────────────────────────────
//
// "app" — the WhatsApp Desktop app, through a whatsapp:// link. The app is
//         already running, so the chat opens in it at once with the message
//         typed in. The fast option, and the one to use when it is installed.
// "web" — WhatsApp Web, in a NEW tab every time, which loads WhatsApp from
//         scratch (several seconds).
//
// Web cannot reuse an open WhatsApp tab, and that is WhatsApp's doing, not
// ours: web.whatsapp.com sends `Cross-Origin-Opener-Policy: same-origin`,
// which cuts every link between this page and the tab it opened — so a named
// window cannot be found again — and sending a tab to a new /send link reloads
// WhatsApp Web anyway. No website can open a chat inside a WhatsApp Web tab
// that is already loaded.
//
// A choice per browser, remembered here, because it depends on what is
// installed on the machine in front of the recruiter.
export const CHAT_OPENERS = [
  {
    key: "app",
    label: "WhatsApp app (fast)",
    hint: "Opens the chat in the WhatsApp Desktop app at once — no reload. The app must be installed and signed in.",
  },
  {
    key: "web",
    label: "WhatsApp Web",
    hint: "Opens a new WhatsApp Web tab each time, which takes a few seconds to load.",
  },
];
const OPENER_STORAGE_KEY = "ats.whatsapp.opener";

export function getChatOpener() {
  try {
    return window.localStorage.getItem(OPENER_STORAGE_KEY) === "app" ? "app" : "web";
  } catch {
    return "web";
  }
}

export function setChatOpener(key) {
  try {
    window.localStorage.setItem(OPENER_STORAGE_KEY, key === "app" ? "app" : "web");
  } catch {
    // Not remembered; this click still uses what was chosen.
  }
}

/**
 * Opens the chat with the message typed in.
 *
 * On a phone, and in the phone app: wa.me, which hands over to the WhatsApp
 * app itself. On a computer: the Desktop app or WhatsApp Web, per the
 * recruiter's choice above.
 */
export function openPrescreenChat(phoneDigits, message, { native = false, opener = "web" } = {}) {
  const text = encodeURIComponent(message); // keeps line breaks, ₹ and emoji intact
  const onPhone = native || /Android|iPhone|iPad/i.test(navigator.userAgent || "");
  if (onPhone) {
    window.open(`https://wa.me/${phoneDigits}?text=${text}`, "_blank");
    return;
  }
  if (opener === "app") {
    // A protocol link hands over to the installed app and leaves this page
    // where it is. The browser asks "Open WhatsApp?" the first time; ticking
    // "Always allow" stops it asking.
    window.location.href = `whatsapp://send?phone=${phoneDigits}&text=${text}`;
    return;
  }
  window.open(`https://web.whatsapp.com/send?phone=${phoneDigits}&text=${text}`, "_blank");
}
